import { useState } from 'react'
import type { FormEvent } from 'react'
import { useAuth } from './AuthContext'
import { EyeIcon } from '../components/icons'
import { PASSWORD_RULES } from '../lib/companyRules'

/**
 * The screen somebody sees once, on their first sign in.
 *
 * They were given a password by whoever set their account up. It got them this
 * far and it stops working here. From this screen on, the only person who
 * knows their password is them, which is what makes the one written in a
 * message safe to send.
 */

/**
 * Checked here as she types so the answer is instant, and checked again by
 * Cognito, which is the one that decides.
 */
const RULES = PASSWORD_RULES

export function ChoosePasswordScreen() {
  const { choosePassword, cancelPasswordChoice } = useAuth()
  const [password, setPassword] = useState('')
  const [again, setAgain] = useState('')
  const [shown, setShown] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const unmet = RULES.filter((r) => !r.ok(password))
  const matches = password.length > 0 && password === again
  const ready = unmet.length === 0 && matches

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!ready) {
      setError(matches ? 'That password is not strong enough yet.' : 'The two passwords do not match.')
      return
    }
    setBusy(true)
    setError('')
    try {
      await choosePassword(password)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That password could not be set. Try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="login">
      <div className="login__card">
        <div className="login__brand">
          <span className="mark" aria-hidden="true">✓</span>
          <span className="topbar__name">Stello</span>
        </div>
        <div>
          <h1>Choose your password</h1>
          <p className="lede">
            The password you were sent works once. Pick your own to finish signing in.
          </p>
        </div>

        <form onSubmit={submit} noValidate>
          <div className="field">
            <label htmlFor="new-password"><span>New password</span></label>
            <div className="field__with-button">
              <input
                id="new-password"
                type={shown ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                required
              />
              <button
                type="button"
                className="reveal"
                onClick={() => setShown(!shown)}
                aria-label={shown ? 'Hide password' : 'Show password'}
                aria-pressed={shown}
                title={shown ? 'Hide password' : 'Show password'}
              >
                <EyeIcon shown={shown} />
              </button>
            </div>
          </div>

          {/*
            The rules are on screen from the start and tick as they are met,
            rather than appearing as a refusal after the attempt. Being told
            what is wanted beats being told afterwards that you guessed wrong.
          */}
          <ul className="rules" aria-label="Password rules">
            {RULES.map((r) => {
              const met = r.ok(password)
              return (
                <li key={r.label} className={met ? 'rules__item is-met' : 'rules__item'}>
                  <span aria-hidden="true">{met ? '✓' : '·'}</span>
                  {r.label}
                  <span className="sr-only">{met ? ' met' : ' not met yet'}</span>
                </li>
              )
            })}
          </ul>

          <label className="field">
            <span>Type it again</span>
            <input
              type="password"
              autoComplete="new-password"
              value={again}
              onChange={(e) => setAgain(e.target.value)}
              required
            />
          </label>
          {again.length > 0 && !matches && (
            <p className="hint hint--bad">The two passwords do not match.</p>
          )}

          {error && (
            <div className="banner banner--bad" role="alert">
              <span aria-hidden="true">!</span>
              {error}
            </div>
          )}

          <button type="submit" className="btn btn--primary" disabled={busy || !ready}>
            {busy ? 'Saving…' : 'Save and sign in'}
          </button>
          <button type="button" className="btn btn--quiet" onClick={cancelPasswordChoice}>
            Back to sign in
          </button>
        </form>
      </div>
    </main>
  )
}
